// Centralised axios instance. Responsibilities:
//   1. Attach the in-memory access token as Bearer on every request.
//   2. On 401, fire a single /v1/auth/refresh call and retry the original
//      request. Multiple in-flight 401s share the same refresh promise so
//      we never trigger refresh storms.
//   3. Surface envelope errors as ApiError instances with code/message.
//
// The refresh token lives in an httpOnly Secure SameSite=Lax cookie
// (`luckydesk_refresh`) — JS code in this SPA can't read it; we trust
// the browser to send it with the refresh call.

import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";

export type Envelope<T> = { data: T; error?: never } | { data?: never; error: ApiErrorBody };

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: Array<{ field?: string; message: string }>;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
    public details?: ApiErrorBody["details"],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let accessToken: string | null = null;
let activeOrgID: string | null = null;
let refreshPromise: Promise<string> | null = null;
let onLogout: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ensureAccessToken devolve um token de acesso VÁLIDO, disparando o refresh
// quando o atual está ausente ou perto de expirar (audit 2026-07 FE-M3). Usado
// pelo WebSocket admin: a reconexão reusava o token em memória no laço de backoff
// e, se ele tivesse expirado, autenticava para sempre com um token morto — o
// realtime ficava silenciosamente inativo até um 401 REST refrescar. Devolve null
// se o refresh falhar (sessão realmente encerrada).
export async function ensureAccessToken(): Promise<string | null> {
  if (accessToken && !isExpiringSoon(accessToken)) return accessToken;
  try {
    return await ensureRefresh();
  } catch {
    return null;
  }
}

// isExpiringSoon decodifica o exp do JWT e responde se falta menos de 30s (folga
// de clock). Token opaco/ilegível → false (deixa o servidor decidir).
function isExpiringSoon(tok: string): boolean {
  try {
    const parts = tok.split(".");
    if (parts.length < 2) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" && payload.exp * 1000 - Date.now() < 30_000;
  } catch {
    return false;
  }
}

// setActiveOrgID pins the superadmin "org switcher" header. When set, every
// outgoing request carries `X-Org-Id: <id>`. The backend honours the header
// only when the caller holds `org:manage_all`, so a regular user setting it
// in dev tools cannot escalate.
export function setActiveOrgID(id: string | null) {
  activeOrgID = id;
}

export function getActiveOrgID(): string | null {
  return activeOrgID;
}

export function registerLogoutHandler(fn: () => void) {
  onLogout = fn;
}

// Build the http instance.
export const api: AxiosInstance = axios.create({
  baseURL: "/",
  withCredentials: true, // for the refresh cookie
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  if (accessToken && !config.headers?.Authorization) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (activeOrgID && !config.headers?.["X-Org-Id"]) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)["X-Org-Id"] = activeOrgID;
  }
  return config;
});

interface RetryableConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _skipRefresh?: boolean;
}

// Endpoints de autenticação (login/refresh/logout/aceite de convite/reset de
// senha) NÃO devem disparar o refresh-retry: um 401 aí é a PRÓPRIA operação de
// auth falhando (ex.: login exige TOTP → 401 TOTP_REQUIRED). Se o interceptor
// tentasse refresh, o erro original (com o código que a tela precisa tratar,
// como TOTP_REQUIRED) seria substituído pelo erro do refresh e ainda dispararia
// logout — a tela de login caía no genérico "algo deu errado".
function isAuthEndpoint(url: string | undefined): boolean {
  return !!url && url.includes("/auth/");
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError<Envelope<unknown>>) => {
    const cfg = error.config as RetryableConfig | undefined;
    const status = error.response?.status;
    if (status === 401 && cfg && !cfg._retry && !cfg._skipRefresh && !isAuthEndpoint(cfg.url)) {
      cfg._retry = true;
      try {
        // Token que ESTE request usou e o servidor rejeitou → o refresh precisa
        // devolver um DIFERENTE (não pode reusar o rejeitado no double-check).
        const auth = (cfg.headers as Record<string, string> | undefined)?.Authorization;
        const knownBad = typeof auth === "string" && auth.startsWith("Bearer ") ? auth.slice(7) : null;
        const token = await ensureRefresh(knownBad);
        cfg.headers = cfg.headers ?? {};
        (cfg.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        return api.request(cfg);
      } catch (refreshErr) {
        if (onLogout) onLogout();
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(toApiError(error));
  },
);

// --- Coordenação de refresh ENTRE ABAS -------------------------------------
// O refresh token é de USO ÚNICO com rotação + detecção de replay que revoga a
// FAMÍLIA inteira no backend (internal/auth/app/service.go). Se DUAS abas (ex.:
// Monitoração + a conversa aberta em nova aba) renovam ao mesmo tempo, ambas
// mandam o MESMO refresh token → a 2ª parece replay → a família é revogada →
// TODAS as abas perdem a sessão. Coordenamos por:
//   1) Web Locks API: só UMA aba renova por vez → nunca há dois /auth/refresh
//      concorrentes com o mesmo token → sem replay → sem revogação da família.
//   2) BroadcastChannel: a aba que renovou compartilha o novo access token com
//      as abas irmãs, que o ADOTAM em vez de renovar de novo → todas convergem
//      na sessão atual (evita "ping-pong" de rotação/revogação mútua).
// Segurança: BroadcastChannel é ESTRITAMENTE mesma-origem; o token já é acessível
// ao JS mesma-origem (é o Bearer em memória) e NÃO é persistido em lugar nenhum
// (nada de localStorage) — não amplia a superfície. Ambas as APIs degradam com
// segurança se ausentes (o dedup por aba via refreshPromise continua valendo).
let authChannel: BroadcastChannel | null = null;
try {
  authChannel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("lk-auth-token") : null;
} catch {
  authChannel = null;
}
if (authChannel) {
  authChannel.onmessage = (ev: MessageEvent) => {
    const msg = ev.data as { type?: string; token?: unknown } | null;
    if (msg && msg.type === "token" && typeof msg.token === "string" && canAdoptToken(msg.token, accessToken)) {
      accessToken = msg.token;
    }
  };
}

function broadcastToken(tok: string): void {
  try {
    authChannel?.postMessage({ type: "token", token: tok });
  } catch {
    /* canal indisponível → segue sem compartilhar */
  }
}

// tokenClaim lê uma claim do payload (base64url) de um JWT; null se ilegível.
function tokenClaim<T>(tok: string, claim: string): T | null {
  try {
    const parts = tok.split(".");
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload[claim] as T;
  } catch {
    return null;
  }
}
function tokenExp(tok: string): number | null {
  const v = tokenClaim<number>(tok, "exp");
  return typeof v === "number" ? v : null;
}
function tokenSub(tok: string): string | null {
  const v = tokenClaim<string>(tok, "sub");
  return typeof v === "string" && v ? v : null;
}

// isNewerToken: o candidato expira DEPOIS do atual (ou não há atual)? Candidato
// ilegível nunca substitui um token legível (defensivo contra mensagem malformada).
export function isNewerToken(candidate: string, current: string | null): boolean {
  if (!current) return true;
  const c = tokenExp(candidate);
  if (c === null) return false;
  const cur = tokenExp(current);
  if (cur === null) return true;
  return c > cur;
}

// canAdoptToken decide se ESTA aba adota um access token transmitido por uma aba
// IRMÃ. Regras de SEGURANÇA (achado adversarial): (1) só adota se JÁ há sessão
// nesta aba — nunca "loga" uma aba deslogada; (2) candidato precisa ser JWT
// parseável (exp numérico) — ignora lixo/forja; (3) precisa ser do MESMO usuário
// (claim `sub`) — o cookie de refresh é ÚNICO e é sobrescrito na troca de conta,
// então sem esta amarração uma aba do usuário X adotaria o token de Y (confusão
// de conta); (4) precisa expirar DEPOIS do atual — nunca rebaixa. Exportada p/ teste.
export function canAdoptToken(candidate: string, current: string | null): boolean {
  if (!current) return false;
  if (tokenExp(candidate) === null) return false;
  const cs = tokenSub(candidate);
  const curs = tokenSub(current);
  if (!cs || !curs || cs !== curs) return false;
  return isNewerToken(candidate, current);
}

// withRefreshLock serializa o refresh entre TODAS as abas da origem (Web Locks).
// A Web Locks API exige CONTEXTO SEGURO — presente em prod (HTTPS) e no dev via
// localhost. Em origem NÃO-segura (ex.: http://<ip-lan>:5173) `navigator.locks` é
// undefined e caímos no fn() direto (sem coordenação entre abas — use localhost aí).
// O dedup por aba (refreshPromise) segue valendo dentro da mesma aba.
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const nav = navigator as Navigator & { locks?: { request?: unknown } };
  if (nav.locks && typeof nav.locks.request === "function") {
    return (nav.locks as LockManager).request("lk-auth-refresh", { mode: "exclusive" }, fn) as Promise<T>;
  }
  return fn();
}

// ensureRefresh renova o access token, serializando entre abas (Web Lock).
// `knownBad`: o token que ACABOU de ser rejeitado num 401 (caminho reativo do
// interceptor). O double-check só pula o /refresh se o token atual for FRESCO E
// DIFERENTE do rejeitado — ou seja, uma aba irmã já renovou e nós adotamos o token
// novo (evita ping-pong). Se o atual É o rejeitado (ou está expirando/ausente),
// renova de fato — senão devolveríamos um token morto e o retry falharia sem
// disparar logout (achado adversarial HIGH). Chamada proativa (WS/expiring) passa
// knownBad indefinido → double-check só olha isExpiringSoon.
async function ensureRefresh(knownBad?: string | null): Promise<string> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      return await withRefreshLock(async () => {
        if (accessToken && accessToken !== knownBad && !isExpiringSoon(accessToken)) return accessToken;
        const resp = await axios.post<Envelope<{ access_token: string }>>(
          "/v1/auth/refresh",
          {},
          // timeout explícito: este POST roda DENTRO do lock exclusivo; sem teto,
          // um refresh pendurado seguraria o lock e congelaria o refresh de TODAS
          // as abas (achado adversarial HIGH). O axios cru não herda o timeout da
          // instância `api`.
          { withCredentials: true, timeout: 15_000 },
        );
        const env = resp.data;
        if (!("data" in env) || !env.data) {
          throw new ApiError("REFRESH_FAILED", "no data in refresh response", resp.status);
        }
        accessToken = env.data.access_token;
        broadcastToken(accessToken);
        return accessToken;
      });
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

function toApiError(err: AxiosError<Envelope<unknown>>): ApiError {
  if (err.response?.data && "error" in err.response.data && err.response.data.error) {
    const body = err.response.data.error;
    return new ApiError(body.code, body.message, err.response.status, body.details);
  }
  return new ApiError(
    "NETWORK",
    err.message || "network error",
    err.response?.status ?? 0,
  );
}

// Typed helpers — every endpoint wrapper in services/* calls these so
// the envelope unwrap happens in exactly one place.

export async function getData<T>(url: string, params?: object): Promise<T> {
  const resp = await api.get<Envelope<T>>(url, { params });
  return unwrap(resp.data);
}

export async function postData<T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig): Promise<T> {
  const resp = await api.post<Envelope<T>>(url, body, config);
  if (resp.status === 204) return undefined as T;
  return unwrap(resp.data);
}

export async function patchData<T, B = unknown>(url: string, body?: B): Promise<T> {
  const resp = await api.patch<Envelope<T>>(url, body);
  if (resp.status === 204) return undefined as T;
  return unwrap(resp.data);
}

export async function putData<T, B = unknown>(url: string, body?: B): Promise<T> {
  const resp = await api.put<Envelope<T>>(url, body);
  if (resp.status === 204) return undefined as T;
  return unwrap(resp.data);
}

export async function deleteData<T = void>(url: string, body?: unknown): Promise<T> {
  const resp = await api.delete<Envelope<T>>(url, { data: body });
  if (resp.status === 204) return undefined as T;
  return unwrap(resp.data);
}

function unwrap<T>(env: Envelope<T>): T {
  if ("error" in env && env.error) {
    throw new ApiError(env.error.code, env.error.message, 0, env.error.details);
  }
  return (env as { data: T }).data;
}
